'use client'

import { useState } from 'react'
import { Spacer, Button, Cell, Text } from '@vinted/web-ui'

import { INBOX_URL } from '@marketplace-web/messaging/navigation-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { navigateToPage, reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useSession } from '@marketplace-web/shared/session-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'

import DeleteConversationModal from '../DeleteConversationModal'
import UserBlockModal from '../UserBlockModal'

type Props = {
  userLogin: string
  oppositeUserId: string
}

const BlockedUserInfoMessage = ({ userLogin, oppositeUserId }: Props) => {
  const translate = useTranslate('conversation.blocked_user_info')
  const [isUnblockModalVisible, setIsUnblockModalVisible] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const conversationId = useConversationContext().conversation?.id
  const currentUserId = useSession().user?.id
  const breakpoints = useBreakpoint()

  const handleUnblockConfirm = () => {
    setIsUnblockModalVisible(false)
    reloadPage()
  }

  const handleToggleUnblockModal = () => {
    setIsUnblockModalVisible(!isUnblockModalVisible)
  }

  const handleToggleDeleteModal = () => {
    setIsDeleteModalVisible(!isDeleteModalVisible)
  }

  const handleDeleteConfirm = () => {
    setIsDeleteModalVisible(false)
    navigateToPage(INBOX_URL)
  }

  if (!conversationId || !currentUserId) return null

  return (
    <>
      <Cell
        title={translate('title')}
        body={
          <>
            <Text as="span" text={translate('body')} />
            <Spacer size="large" />
            <Button
              styling="filled"
              text={translate('actions.unblock')}
              onClick={handleToggleUnblockModal}
              inline={!breakpoints.phones}
              testId="blocked-user-action-unblock"
            />
            <Spacer orientation={breakpoints.phones ? 'horizontal' : 'vertical'} size="large" />
            <Button
              text={translate('actions.delete_conversation')}
              theme="warning"
              onClick={handleToggleDeleteModal}
              inline={!breakpoints.phones}
              testId="blocked-user-action-delete-conversation"
            />
          </>
        }
      />
      <UserBlockModal
        username={userLogin}
        isUserBlocked
        userId={Number(oppositeUserId)}
        isVisible={isUnblockModalVisible}
        onCancel={handleToggleUnblockModal}
        onConfirm={handleUnblockConfirm}
      />
      <DeleteConversationModal
        conversationId={conversationId}
        show={isDeleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleToggleDeleteModal}
      />
    </>
  )
}

export default BlockedUserInfoMessage
